import React, { useState, useMemo, useEffect } from "react";
import * as api from "../api";
import {
  AlertTriangle, Banknote, Bot, Check, CheckCircle2, ChevronDown, Clock, Copy, CreditCard,
  Crown, DollarSign, Edit3, Eye, Gamepad2, KeyRound, Layers, Lock,
  MessageCircle, Package, Plus, RefreshCw, Search, ShieldCheck, ShoppingCart,
  Sparkles, Trash2, TrendingUp, Trophy, Upload, User, Wallet, X, XCircle, Gift, FileSpreadsheet, Flame
} from "lucide-react";
import { AdminDivisaTab } from "./admin/AdminDivisaTab";
import { AutoCloseCard } from "./admin/AutoCloseCard";
import { AdminPagosTab } from "./admin/AdminPagosTab";
import { AdminPushToggle } from "../components/admin/AdminPushToggle";
import { AdminAdjustmentPanel } from "../components/admin/AdminAdjustmentPanel";
import { AnalyticsPanel } from "../components/admin/AnalyticsPanel";
import { CatalogPortability } from "../components/admin/CatalogPortability";
import { ResetTestDataCard } from "../components/admin/ResetTestDataCard";
import { TopUsersRanking } from "../components/admin/TopUsersRanking";
import { SalesPanel } from "../components/admin/SalesPanel";
import { InlineCostEdit } from "../components/admin/InlineCostEdit";
import { OrderForensicPanel } from "../components/admin/OrderForensicPanel";
import { OrderAutomationItems } from "../components/admin/OrderAutomationItems";
import { ProviderAutomationPanel } from "../components/admin/ProviderAutomationPanel";
import { UserManagement } from "../components/admin/UserManagement";
import { CatalogManagement } from "../components/admin/CatalogManagement";
import { WithdrawalsPanel } from "../components/admin/WithdrawalsPanel";
import { TournamentsAdminPanel } from "../components/admin/TournamentsAdminPanel";
import { FinancialReportsPanel } from "../components/admin/FinancialReportsPanel";
import { ChatButton } from "../components/chat/ChatButton";
import { CredRow } from "../components/shared/CredRow";
import { StatusBadge, UserStatusBadge } from "../components/shared/StatusBadges";
import { useAppContext } from "../AppContext";
import { reviewReasonLabel } from "../lib/reviewReasons";
import { bsAmount } from "../lib/bsFormat";

/* -----------------------------------------------------------------------------
 * AdminPage — full admin panel extracted from the previously monolithic
 * App.js. State/handlers are read via useAppContext(); the JSX is preserved
 * verbatim so behaviour is identical.
 * --------------------------------------------------------------------------- */
export function AdminPage() {
  const ctx = useAppContext();
  const [approvePaymentOrder, setApprovePaymentOrder] = useState(null);
  const [approvePaymentBusy, setApprovePaymentBusy] = useState(false);
  // Pending withdrawals badge — módulo independiente de pedidos: poll propio
  // y ligero (15s) solo para el contador del tab "RETIROS".
  const [pendingWithdrawalsCount, setPendingWithdrawalsCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await api.listAdminWithdrawals("Pendiente");
        if (!cancelled) setPendingWithdrawalsCount(rows.length);
      } catch (e) { /* silencioso — no bloquea el resto del admin */ }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  const {
    // Core / role state
    user, adminTab, setAdminTab, showToast,
    // Dashboard metrics (memoised in App.js)
    totalRevenueUsd, pendingOrdersCount, processingOrdersCount,
    completedOrdersCount, needsReviewOrdersCount,
    incompletePaymentOrdersCount,
    // Format / conversion helpers
    exchangeRate, rateReady, usdToBs, formatBs, copyToClipboard, setExchangeRate,
    // Orders admin
    orders, setOrders, orderFilter, setOrderFilter, loadOrders,
    loadMoreAdminOrders, adminLoadMoreBusy, adminHasMoreOrders,
    handleApprovePayment, handleUpdateOrderStatus, handleReopenOrder, handleConfirmRecordedDelivery,
    openRejectOrder, setPreviewProofUrl, setChatOrder,
    setAdjustOrderModal, handleApproveAdjustment,
    // Manual override modal (NEEDS_REVIEW correction)
    setManualVerifyOrder,
    // Games / packages admin
    games, setGames, expandedGames, toggleExpandedGame,
    openCreateGameModal, openEditGameModal, setDeleteGameConfirm,
    handleToggleGameActive, handleUpdateGameCredType,
    setShowAddPkgModal, setEditingPkg, resetPkgForm,
    setPkgGameId, setPkgNameInput, setPkgPriceUsdInput,
    setPkgDescriptionInput, setPkgImageInput,
    setPkgResellerPriceUsdInput, setPkgRequiresVerify,
    setPkgIsBestSellerInput, setPkgVerifyTitle,
    setPkgVerifyDescription, setPkgVerifyImageUrl, setPkgVerifyStepsText,
    handleDeletePackage,
    // Payment methods (Cuentas)
    paymentMethods, handleTogglePmActive, handleDeleteBank,
    setShowAddBankModal, openEditPm, setBankForm, setEditingPm,
    EMPTY_BANK_FORM,
    // Users
    users, loadingUsers, loadUsers, userRoleFilter, setUserRoleFilter,
    userSearch, setUserSearch, requestStatusChange,
    handleToggleUserRole, setDeleteUserConfirm, openClientHistory,
    handleChangeUserStatus,
    // Divisa & soporte
    rateInput, setRateInput, handleSaveRate,
    supportWhatsapp, supportWhatsappInput, setSupportWhatsappInput,
    handleSaveSupportWhatsapp, savingSupportWa,
    // Social networks
    adminSocialNets, snForm, setSnForm, snBusy, handleSaveSocial,
    handleToggleSocial, handleDeleteSocial,
    // Banners
    banners, bannerForm, setBannerForm, bannerBusy,
    handleBannerImageUpload, bannerUploadingIdx, handleRemoveBannerImage,
    handleSaveBanner, handleToggleBannerActive, handleDeleteBanner,
    resetBannerForm,
    // Seguridad (2FA + site status + change password)
    siteStatus, siteStatusForm, setSiteStatusForm, siteStatusBusy,
    handleToggleSiteOpen, handleSaveClosureReason, loadSiteStatus,
    twoFAEnabled, twoFASetup, twoFASetupCode, setTwoFASetupCode,
    twoFAEnableBusy, handleStartTwoFASetup, handleEnableTwoFA,
    setTwoFASetup, twoFADisablePwd, setTwoFADisablePwd,
    twoFADisableBusy, handleDisableTwoFA,
    pwdOld, setPwdOld, pwdNew, setPwdNew, pwdBusy, handleChangePassword,
  } = ctx;

  // Free-text search over loaded orders. Kept local (no need to reach for
  // context) because it only affects the pedidos view and doesn't need to
  // persist across role toggles.
  const [orderSearch, setOrderSearch] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [expandedClosedOrderIds, setExpandedClosedOrderIds] = useState(() => new Set());

  const toggleClosedOrderDetails = (orderId) => {
    setExpandedClosedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  // Si se reabre un pedido, debe volver al flujo activo y no conservar el
  // estado de colapso de su cierre anterior.
  useEffect(() => {
    setExpandedClosedOrderIds((current) => {
      const activeIds = new Set((orders || [])
        .filter((order) => ["Completado", "Rechazado"].includes(order.status))
        .map((order) => order.id));
      const next = new Set([...current].filter((id) => activeIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [orders]);

  // Precomputed lowercase, trimmed haystack per order for the search
  // filter — avoids re-normalizing on every keystroke × every order.
  const orderSearchIndex = useMemo(() => {
    const map = new Map();
    for (const o of orders || []) {
      const parts = [
        o.id,
        o.orderNumber != null ? `#${String(o.orderNumber).padStart(4, "0")}` : "",
        o.referenceNumber,
        o.ai_reference,
        o.userEmail,
        o.userUsername,
        o.userName,
        o.userPhone,
        ...(o.additionalPayments || []).map((ap) => ap.referenceNumber || ap.aiReference || ""),
      ];
      map.set(o.id, parts.filter(Boolean).join(" ").toLowerCase());
    }
    return map;
  }, [orders]);

  const searchQuery = orderSearch.trim().toLowerCase();
  const matchesSearch = (o) => {
    if (!searchQuery) return true;
    const hay = orderSearchIndex.get(o.id) || "";
    // Digit-only tokens should also match against digits-only extractions
    // of phone / reference (e.g. "584121..." finds "+58412 1...").
    return hay.includes(searchQuery) || hay.replace(/\D/g, "").includes(searchQuery.replace(/\D/g, ""));
  };
  const matchesDate = (o) => {
    if (!orderDate) return true;
    if (!o?.createdAt) return false;
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Caracas", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(o.createdAt));
    const date = Object.fromEntries(parts.filter(({ type }) => type !== "literal").map(({ type, value }) => [type, value]));
    return `${date.year}-${date.month}-${date.day}` === orderDate;
  };

  return (
    <>
          <div className="space-y-6" data-testid="admin-panel">
            <div className="relative overflow-hidden bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/25 rounded-2xl px-5 py-4">
              <div className="pointer-events-none absolute -top-14 right-0 w-44 h-44 rounded-full bg-ag7-purple/15 blur-3xl" aria-hidden="true" />
              <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-ag7-purple/15 border border-ag7-purple/50 shadow-[0_0_18px_rgba(147,51,234,0.25)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <ShieldCheck className="w-5 h-5 text-ag7-purple" />
                </div>
                <div>
                  <h2 className="font-heading font-black text-white text-base sm:text-lg uppercase tracking-wide leading-tight">Panel de Control</h2>
                  <p className="text-[10px] sm:text-[11px] font-mono text-[var(--ag7-text-muted)] uppercase tracking-widest">AG7 Store · Administración</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 bg-[var(--ag7-glass)] backdrop-blur-xl border border-[var(--ag7-border)] p-2 rounded-2xl">
              {[
                { id: "dashboard", label: "DASHBOARD", icon: Layers },
                { id: "paquetes", label: "JUEGOS", icon: Package },
                { id: "pedidos", label: "PEDIDOS", icon: ShoppingCart, badge: pendingOrdersCount },
                { id: "automatizacion", label: "AUTOMATIZACIÓN", icon: Bot },
                { id: "pagos", label: "CUENTAS", icon: CreditCard },
                { id: "usuarios", label: "USUARIOS", icon: User },
                { id: "retiros", label: "RETIROS", icon: Banknote, badge: pendingWithdrawalsCount },
                { id: "torneos", label: "TORNEOS", icon: Trophy },
                { id: "top", label: "TOP", icon: Trophy },
                { id: "ganancias", label: "GANANCIAS", icon: TrendingUp },
                { id: "reportes", label: "REPORTES", icon: FileSpreadsheet },
                { id: "divisa", label: "DIVISA", icon: DollarSign },
                { id: "redes", label: "REDES", icon: Sparkles },
                { id: "banners", label: "BANNERS", icon: Package },
                { id: "seguridad", label: "SEGURIDAD", icon: ShieldCheck }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setAdminTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border transition-colors ${adminTab === tab.id ? "bg-ag7-purple/20 text-white border-ag7-purple/60 shadow-[var(--ag7-glow-purple)]" : "text-[var(--ag7-text-muted)] border-transparent hover:bg-ag7-surface-2 hover:text-white"}`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span>{tab.label}</span>
                    {tab.badge > 0 && <span className="w-5 h-5 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-bold">{tab.badge}</span>}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-2">
                {needsReviewOrdersCount > 0 && (
                  <button
                    onClick={() => { setAdminTab("pedidos"); setOrderFilter("needs_review"); }}
                    data-testid="admin-needs-review-counter"
                    title="Ir a pedidos por verificar"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/60 text-amber-200 text-[11px] font-mono font-bold uppercase tracking-widest shadow-[0_0_14px_-4px_rgba(245,158,11,0.7)] animate-pulse hover:bg-amber-500/30"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>Por Verificar</span>
                    <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-900/70 text-amber-100 text-[10px] font-black flex items-center justify-center">
                      {needsReviewOrdersCount}
                    </span>
                  </button>
                )}
                <AdminPushToggle showToast={showToast} />
              </div>
            </div>

            {/* DASHBOARD */}
            {adminTab === "dashboard" && <AnalyticsPanel />}
            {adminTab === "reportes" && <FinancialReportsPanel showToast={showToast} />}

            {/* AUTOMATIZACIÓN DE PROVEEDOR (Free Fire · modo supervisado) */}
            {adminTab === "automatizacion" && <ProviderAutomationPanel showToast={showToast} />}

            {/* ORDERS ADMIN */}
            {adminTab === "pedidos" && (
              <div className="space-y-6" data-testid="admin-orders-view">
                <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl p-6">
                  <h3 className="text-xl font-heading font-extrabold tracking-tight text-white mb-1">Gestión de Pedidos y Comprobantes</h3>
                  <p className="text-xs text-zinc-400">Revisa comprobantes y actualiza estados de recarga.</p>
                </div>

                {/* Free-text search over loaded orders. Combines with the
                    filter chips (AND). Matches referencia bancaria (own +
                    AI-extracted + additional payments), nombre / usuario /
                    email del cliente, y teléfono. */}
                <div className="relative">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400/70 pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    placeholder="Buscar por referencia, usuario o teléfono..."
                    data-testid="admin-orders-search-input"
                    className="w-full pl-11 pr-10 py-3 rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] focus:border-ag7-cyan/60 focus:outline-none text-white text-sm font-mono placeholder:text-zinc-500 transition-all shadow-[0_0_0_-2px_rgba(34,211,238,0)] focus:shadow-[0_0_20px_-8px_rgba(34,211,238,0.7)]"
                  />
                  {orderSearch && (
                    <button
                      type="button"
                      onClick={() => setOrderSearch("")}
                      data-testid="admin-orders-search-clear"
                      aria-label="Limpiar búsqueda"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="admin-orders-date-filter">
                  <label className="flex flex-col gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500">Buscar por fecha
                    <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} data-testid="admin-orders-date-input" className="rounded-xl border border-[var(--ag7-border-soft)] bg-ag7-surface px-3 py-2 text-sm text-white outline-none focus:border-ag7-cyan/60" />
                  </label>
                  {orderDate && <button type="button" onClick={() => setOrderDate("")} data-testid="admin-orders-date-clear" className="px-3 py-2 text-xs font-bold text-cyan-200 hover:text-white">Limpiar fecha</button>}
                </div>

                {/* Filter chip bar */}
                {(() => {
                  // Counts reflect the CURRENT search query so admins see
                  // "How many rejected receipts match this reference?" at a
                  // glance. When the query is empty, this collapses to the
                  // ordinary orders count.
                  const searched = orders?.filter(o => matchesSearch(o) && matchesDate(o));
                  const countBy = (pred) => searched?.filter(pred).length;
                  const chips = [
                    { id: "all", label: "Todos", count: searched.length, color: "zinc" },
                    { id: "needs_review", label: "Pagos por Verificar", count: countBy(o => o.verification_status === "NEEDS_REVIEW" && !["Completado", "Rechazado"].includes(o.status)), color: "amber" },
                    { id: "incomplete", label: "Pagos Incompletos", count: countBy(o => o.status === "Pago Incompleto"), color: "orange" },
                    { id: "pending", label: "Pendientes", count: countBy(o => o.status === "Pendiente"), color: "yellow" },
                    { id: "processing", label: "En Proceso", count: countBy(o => o.status === "En Proceso"), color: "cyan" },
                    { id: "rejected", label: "Rechazados", count: countBy(o => o.status === "Rechazado"), color: "red" },
                  ];
                  return (
                    <div className="flex flex-wrap items-center gap-2" data-testid="admin-order-filter-bar">
                      {chips.map((c) => {
                        const active = orderFilter === c.id;
                        const palette = {
                          zinc: active ? "bg-zinc-700 border-zinc-500 text-white" : "bg-ag7-surface border-[var(--ag7-border-soft)] text-zinc-400 hover:text-white",
                          amber: active ? "bg-amber-500/25 border-amber-400 text-amber-200 shadow-[0_0_18px_-6px_rgba(245,158,11,0.7)]" : "bg-amber-950/20 border-amber-500/40 text-amber-300/90 hover:text-amber-200",
                          orange: active ? "bg-orange-500/25 border-orange-400 text-orange-200 shadow-[0_0_18px_-6px_rgba(249,115,22,0.7)]" : "bg-orange-950/20 border-orange-500/40 text-orange-300/90 hover:text-orange-200",
                          yellow: active ? "bg-yellow-500/20 border-yellow-400 text-yellow-200" : "bg-ag7-surface border-[var(--ag7-border-soft)] text-zinc-400 hover:text-yellow-200",
                          cyan: active ? "bg-cyan-500/20 border-cyan-400 text-cyan-200" : "bg-ag7-surface border-[var(--ag7-border-soft)] text-zinc-400 hover:text-cyan-200",
                          red: active ? "bg-red-500/20 border-red-500 text-red-200" : "bg-ag7-surface border-[var(--ag7-border-soft)] text-zinc-400 hover:text-red-200",
                        }[c.color];
                        return (
                          <button
                            key={c.id}
                            onClick={() => setOrderFilter(c.id)}
                            data-testid={`admin-order-filter-${c.id}`}
                            className={`px-3 py-2 rounded-xl border text-[11px] font-bold uppercase tracking-widest font-mono transition-all flex items-center gap-2 ${palette}`}
                          >
                            {c.id === "needs_review" && <AlertTriangle className="w-3.5 h-3.5" />}
                            {c.id === "incomplete" && <AlertTriangle className="w-3.5 h-3.5" />}
                            <span>{c.label}</span>
                            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-black ${active ? "bg-black/40" : "bg-zinc-800"}`}>{c.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {(() => {
                  const filtered = orders?.filter(o => {
                    if (!matchesSearch(o) || !matchesDate(o)) return false;
                    if (orderFilter === "all") return true;
                    if (orderFilter === "needs_review") return o.verification_status === "NEEDS_REVIEW" && !["Completado", "Rechazado"].includes(o.status);
                    if (orderFilter === "incomplete") return o.status === "Pago Incompleto";
                    if (orderFilter === "pending") return o.status === "Pendiente";
                    if (orderFilter === "processing") return o.status === "En Proceso";
                    if (orderFilter === "rejected") return o.status === "Rechazado";
                    return true;
                  });
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-12 text-zinc-500 text-sm" data-testid="admin-orders-empty">
                        {searchQuery
                          ? <>No hay pedidos que coincidan con <strong className="text-cyan-300 font-mono">&quot;{orderSearch}&quot;</strong>. Prueba con menos texto o carga más pedidos.</>
                          : orderDate
                            ? <>No hay pedidos cargados para el <strong className="text-cyan-300 font-mono">{orderDate}</strong>. Prueba a cargar más pedidos.</>
                          : orderFilter === "needs_review"
                            ? "No hay comprobantes por verificar. Todo al día."
                            : orderFilter === "incomplete"
                              ? "No hay pedidos con pago incompleto en este momento."
                              : "No hay pedidos en este filtro."}
                      </div>
                    );
                  }
                  return filtered?.map(order => {
                  const isClosedOrder = ["Completado", "Rechazado"].includes(order.status);
                  const isClosedOrderExpanded = expandedClosedOrderIds.has(order.id);
                  const hasDeliveredItem = (order.items || []).some((item) =>
                    item.deliveredManually || item.deliveredAutomatically
                    || ["CONFIRMED", "CANCELLED_MANUAL"].includes(item.provider_item_status)
                  );
                  return (
                  <div key={order.id} data-testid={`admin-order-card-${order.id}`} className="rounded-xl bg-ag7-surface border border-[var(--ag7-border-soft)] hover:border-ag7-purple/30 transition-colors p-3 sm:p-4 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--ag7-border)] pb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-ag7-cyan text-sm">
                            {order.orderNumber != null ? `#${String(order.orderNumber).padStart(4, "0")}` : order.id}
                          </span>
                          <StatusBadge status={order.status} />
                          {order.has_referral_attribution && (
                            <span
                              data-testid={`admin-order-referral-badge-${order.id}`}
                              title="El cliente está vinculado mediante una atribución de referido guardada"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-fuchsia-400/45 bg-fuchsia-500/10 text-fuchsia-200 text-[10px] font-mono font-bold uppercase tracking-widest"
                            >
                              <Gift className="w-3 h-3" />
                              Referido
                            </span>
                          )}
                          {order.verification_status === "NEEDS_REVIEW" && !["Completado", "Rechazado"].includes(order.status) && (
                            <span
                              data-testid={`admin-order-needs-review-badge-${order.id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/60 text-amber-200 text-[10px] font-mono font-bold uppercase tracking-widest shadow-[0_0_10px_-2px_rgba(245,158,11,0.6)] animate-pulse"
                            >
                              <AlertTriangle className="w-3 h-3" />
                              Por Verificar
                            </span>
                          )}
                          {order.bank_registered === false && (
                            <span
                              data-testid={`admin-order-unregistered-bank-badge-${order.id}`}
                              title="Banco no registrado en el catálogo. NO es fraude: valida la referencia manualmente."
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/60 text-fuchsia-200 text-[10px] font-mono font-bold uppercase tracking-widest"
                            >
                              <AlertTriangle className="w-3 h-3" />
                              Banco no registrado
                            </span>
                          )}
                          {order.manual_reference_by_client && (
                            <span
                              data-testid={`admin-order-manual-ref-badge-${order.id}`}
                              title="La IA no leyó la referencia — el cliente la escribió a mano. Verifica el comprobante."
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/60 text-orange-200 text-[10px] font-mono font-bold uppercase tracking-widest"
                            >
                              <KeyRound className="w-3 h-3" />
                              Ref. manual
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500 mt-1 font-mono">{new Date(order.createdAt).toLocaleString("es-VE")}</p>
                      </div>
                      <div className="text-right">
                        {(order.items && order.items.length > 1) ? (
                          <>
                            <p className="text-sm font-bold text-white" data-testid={`admin-order-multi-summary-${order.id}`}>
                              {order.items.length} paquetes · {new Set(order.items?.map(i => i.gameName)).size} juego{new Set(order.items?.map(i => i.gameName)).size > 1 ? "s" : ""}
                            </p>
                            <p className="text-[11px] text-zinc-400 line-clamp-1 max-w-[280px]" title={order.items?.map(i => `${i.gameName} · ${i.packageName}`).join(" | ")}>
                              {order.items?.map(i => i.packageName).slice(0, 3).join(", ")}{order.items.length > 3 ? "…" : ""}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm font-bold text-white">{order.gameName} · {order.packageName}</p>
                        )}
                        <p className="text-xs text-ag7-cyan font-mono tabular-nums">Bs {bsAmount(order.priceBs)} · ${bsAmount(order.priceUsd)}</p>
                        <InlineCostEdit
                          order={order}
                          showToast={showToast}
                          onUpdated={(updated) => setOrders(orders?.map(o => o.id === updated.id ? updated : o))}
                        />
                      </div>
                    </div>

                    {isClosedOrder && (
                      <div
                        data-testid={`admin-order-closed-summary-${order.id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-700/80 bg-zinc-950/60 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p data-testid={`admin-order-closed-status-${order.id}`} className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-300">
                            {order.status === "Completado" ? "Recarga finalizada" : "Pedido rechazado"}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-zinc-500">
                            {order.status === "Rechazado" && order.rejectionReason
                              ? `Motivo: ${order.rejectionReason}`
                              : "Los comprobantes, datos y acciones están resguardados en los detalles."}
                          </p>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleClosedOrderDetails(order.id)}
                            data-testid={`admin-order-details-toggle-${order.id}`}
                            aria-expanded={isClosedOrderExpanded}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-600 bg-zinc-800 px-2.5 py-1.5 text-[10px] font-bold text-zinc-100 transition-[background-color,border-color,transform] duration-200 hover:border-ag7-cyan/70 hover:bg-zinc-700"
                          >
                            {isClosedOrderExpanded ? "Ocultar detalles" : "Ver detalles"}
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isClosedOrderExpanded ? "rotate-180" : ""}`} />
                          </button>
                          {!isClosedOrderExpanded && (
                            <button
                              type="button"
                              onClick={() => handleReopenOrder(order.id)}
                              data-testid={`reopen-order-compact-${order.id}`}
                              title="Volver a poner el pedido en proceso"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/60 bg-amber-500/15 px-2.5 py-1.5 text-[10px] font-bold text-amber-200 transition-[background-color,border-color] duration-200 hover:bg-amber-500/25"
                            >
                              <RefreshCw className="h-3.5 w-3.5" /> Reabrir
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={isClosedOrder && !isClosedOrderExpanded ? "hidden" : "contents"}>

                    {/* AI review notes for NEEDS_REVIEW orders */}
                    {order.verification_status === "NEEDS_REVIEW" && !["Completado", "Rechazado"].includes(order.status) && (
                      <div
                        data-testid={`admin-order-review-notes-${order.id}`}
                        className="p-3 rounded-xl bg-amber-950/25 border border-amber-500/40 space-y-3"
                      >
                        <div className="flex items-start gap-2.5">
                          <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-1">
                              Nota de la IA{order.review_reason ? ` · ${reviewReasonLabel(order.review_reason)}` : ""}
                            </p>
                            <p className="text-xs text-amber-100 leading-relaxed">
                              {order.review_notes || "El comprobante requiere verificación manual."}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-500/20">
                          <button
                            onClick={() => setManualVerifyOrder(order)}
                            data-testid={`manual-verify-open-${order.id}`}
                            className="flex-1 min-w-[160px] px-3 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:opacity-90 text-white text-xs font-black flex items-center justify-center gap-1.5 shadow-md"
                          >
                            <Edit3 className="w-4 h-4" />
                            Corrección Manual
                          </button>
                          <button
                            onClick={() => handleApprovePayment(order.id)}
                            data-testid={`approve-payment-${order.id}`}
                            className="flex-1 min-w-[140px] px-3 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-500 hover:opacity-90 text-white text-xs font-black flex items-center justify-center gap-1.5 shadow-md"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Aprobar Pago
                          </button>
                          <button
                            onClick={() => openRejectOrder(order)}
                            data-testid={`reject-payment-${order.id}`}
                            className="flex-1 min-w-[140px] px-3 py-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-500 hover:opacity-90 text-white text-xs font-black flex items-center justify-center gap-1.5 shadow-md"
                          >
                            <XCircle className="w-4 h-4" />
                            Rechazar Pago
                          </button>
                        </div>
                      </div>
                    )}

                    {/* FASE 5 — Panel forense (solo NEEDS_REVIEW / Rechazado) */}
                    <OrderForensicPanel order={order} />

                    <div
                      data-testid={`admin-order-buyer-${order.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-cyan-950/40 to-blue-950/30 border border-cyan-500/30"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">
                          Cliente que creó el pedido
                          {order.userRole === "reseller" && (
                            <span
                              data-testid={`admin-order-reseller-badge-${order.id}`}
                              title="Este pedido fue creado por un revendedor (mayorista)"
                              className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-200 text-[9px] font-black uppercase tracking-widest font-mono"
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              Revendedor
                            </span>
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <button
                            onClick={() => openClientHistory(order.userId)}
                            data-testid={`admin-order-buyer-tap-${order.id}`}
                            className="text-sm font-bold text-white hover:text-cyan-300 underline decoration-cyan-500/40 decoration-dotted underline-offset-4 truncate max-w-full text-left"
                            title="Ver historial y saldo del cliente"
                          >
                            {order.userUsername || order.userName || (order.userEmail ? order.userEmail.split("@")[0] : `Cliente #${(order.userId || "").slice(0, 6)}`)}
                          </button>
                          {order.userPhone && (
                            <span
                              data-testid={`admin-order-phone-${order.id}`}
                              className="text-[11px] font-mono text-cyan-200 flex items-center gap-1"
                              title="Teléfono"
                            >
                              📱 {order.userPhone}
                            </span>
                          )}
                          {order.userRole === "reseller" && (
                            <span
                              data-testid={`admin-order-reseller-badge-${order.id}`}
                              title="Este pedido lo hizo un Revendedor (precio mayorista)"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-400/60 text-amber-100 text-[9px] font-black uppercase tracking-widest font-mono shadow-[0_0_12px_-2px_rgba(245,158,11,0.55)]"
                            >
                              <Crown className="w-3 h-3 text-amber-300" />
                              Revendedor
                            </span>
                          )}
                          {order.creditApplied > 0 && (() => {
                            const isFullCredit = order.bankOrigin === "SALDO A FAVOR" || (order.paidAmountBs || 0) < 0.5;
                            return (
                              <span
                                data-testid={`admin-order-wallet-badge-${order.id}`}
                                title={isFullCredit
                                  ? "Este pedido se pagó 100% con Saldo a Favor del cliente"
                                  : "Este pedido combinó Saldo a Favor + pago bancario"}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest font-mono shadow-[0_0_12px_-2px_rgba(16,185,129,0.55)] ${
                                  isFullCredit
                                    ? "bg-gradient-to-r from-emerald-500/30 to-emerald-400/25 border border-emerald-400/70 text-emerald-100"
                                    : "bg-gradient-to-r from-emerald-500/20 to-purple-500/20 border border-emerald-400/50 text-emerald-100"
                                }`}
                              >
                                <Wallet className="w-3 h-3 text-emerald-300" />
                                {isFullCredit ? "100% Saldo" : "Saldo a Favor"}
                              </span>
                            );
                          })()}
                          {order.is_first_order && (
                            <span
                              data-testid={`admin-order-new-client-badge-${order.id}`}
                              title="Primera recarga de este cliente"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/50 text-pink-200 text-[9px] font-black uppercase tracking-widest font-mono shadow-[0_0_12px_-2px_rgba(236,72,153,0.5)]"
                            >
                              <Sparkles className="w-3 h-3 text-pink-300" />
                              Nuevo cliente
                            </span>
                          )}
                        </div>
                      </div>
                      {order.userPhone && (
                        <button
                          onClick={() => copyToClipboard(order.userPhone, "Teléfono copiado")}
                          data-testid={`admin-order-copy-phone-${order.id}`}
                          className="bg-ag7-surface-2 hover:bg-zinc-700 text-cyan-300 p-2 rounded-lg border border-[var(--ag7-border-soft)]"
                          aria-label="Copiar teléfono del cliente"
                          title="Copiar teléfono"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {(() => {
                      const pm = (paymentMethods || []).find(m => m.id === order.paymentMethodId);
                      // Nice human labels for the 3 canonical types.
                      const typeLabels = {
                        PAGO_MOVIL: "Pago Móvil",
                        TRANSFERENCIA: "Transferencia",
                        USD_PLATFORM: "Pago en $",
                      };
                      const pmType = pm?.type ? typeLabels[pm.type] || pm.type : null;
                      const pmBankPart = pm
                        ? `${pm.bankName || pm.name || "Banco"}${pm.bankCode ? ` (${pm.bankCode})` : ""}`
                        : (order.bankOrigin || "—");
                      const pmLabel = pmType ? `${pmType} · ${pmBankPart}` : pmBankPart;
                      const additionalCount = Array.isArray(order.additionalPayments) ? order.additionalPayments.length : 0;
                      const additionalTotal = additionalCount > 0
                        ? order.additionalPayments?.reduce((a, p) => a + Number(p.paidAmountBs || 0), 0)
                        : 0;
                      const totalPaidBs = Number(order.paidAmountBs || 0) + additionalTotal;
                      const hasMultipleProofs = additionalCount > 0;
                      return (
                        <details data-testid={`admin-order-details-${order.id}`} className="group rounded-xl border border-[var(--ag7-border-soft)] bg-ag7-bg/40">
                          <summary data-testid="admin-page-summary-1" className="list-none cursor-pointer select-none px-3 py-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors">
                            <span className="flex items-center gap-2">
                              <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
                              Ver comprobante y detalles del pago
                            </span>
                            <span className="normal-case text-[10px] text-zinc-500 truncate max-w-[55%] text-right">
                              {hasMultipleProofs ? `${additionalCount + 1} refs` : `Ref ${order.referenceNumber || order.ai_reference || "—"}`} · Bs {Math.round(totalPaidBs)}
                            </span>
                          </summary>
                          <div className="px-3 pb-3 space-y-2">
                          {order.has_referral_attribution && (
                            <div
                              data-testid={`admin-order-referral-detail-${order.id}`}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/35 bg-fuchsia-500/10 px-2.5 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-fuchsia-200"
                            >
                              <Gift className="w-3.5 h-3.5" />
                              Cliente referido
                            </div>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono bg-zinc-950 p-2.5 rounded-lg border border-[var(--ag7-border-soft)]">
                            <div data-testid={`admin-order-ref-${order.id}`} className={hasMultipleProofs ? "col-span-2 sm:col-span-4 space-y-0.5" : ""}>
                              {hasMultipleProofs ? (
                                <>
                                  <span className="text-zinc-500">Pago #1 · </span>Ref: <strong className="text-green-400">{order.referenceNumber || order.ai_reference || "—"}</strong>
                                  {order.additionalPayments?.map((ap, i) => (
                                    <div key={`ref-${i}`} data-testid={`admin-order-ref-${order.id}-${i + 1}`}>
                                      <span className="text-zinc-500">Pago #{i + 2} · </span>Ref: <strong className="text-green-400">{ap.referenceNumber || ap.aiReference || "—"}</strong>
                                      <span className="ml-1 text-cyan-300" data-testid={`admin-order-payment-currency-${order.id}-${i + 1}`}>
                                        · {ap.paymentCurrency === "USD" ? `$${Number(ap.paidAmountUsd || 0).toFixed(2)} USD/USDT` : `Bs ${Number(ap.paidAmountBs || 0).toFixed(2)}`}
                                      </span>
                                      {ap.quotedExchangeRate && <span className="ml-1 text-zinc-500">· tasa Bs {Number(ap.quotedExchangeRate).toFixed(2)} · equiv. Bs {Number(ap.paidAmountBs || 0).toFixed(2)}</span>}
                                      {ap.verifiedAt && <span className="ml-1 text-zinc-600">· {new Date(ap.verifiedAt).toLocaleString("es-VE")}</span>}
                                      {ap.verificationStatus === "NEEDS_REVIEW" && <span className="ml-1 text-[9px] uppercase tracking-widest text-amber-400">en revisión</span>}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <>Ref: <strong className="text-green-400">{order.referenceNumber || order.ai_reference || "—"}</strong></>
                              )}
                              {!hasMultipleProofs && !order.referenceNumber && order.ai_reference && (
                                <span
                                  title="Referencia leída automáticamente de la imagen por la IA"
                                  className="ml-1 text-[9px] uppercase tracking-widest text-cyan-500"
                                >IA</span>
                              )}
                            </div>
                            <div>Banco: <strong className="text-white">{order.bankOrigin || "—"}</strong></div>
                            <div>
                              Pagado: <strong className="text-cyan-400">Bs {Math.round(totalPaidBs)}</strong>
                              {hasMultipleProofs && (
                                <span className="ml-1 text-[9px] font-mono text-zinc-500">
                                  ({additionalCount + 1} pagos)
                                </span>
                              )}
                            </div>
                            <div className="col-span-2 sm:col-span-1" data-testid={`admin-order-payment-method-${order.id}`}>
                              Método: <strong className="text-white">{pmLabel}</strong>
                            </div>
                            {order.surplusBs > 0 && (
                              <div data-testid={`admin-order-surplus-${order.id}`}>
                                Excedente:{" "}
                                <strong className={order.surplusCredited ? "text-green-400" : "text-amber-400"}>
                                  Bs {Math.round(order.surplusBs)}
                                </strong>
                                {!order.surplusCredited && (
                                  <span
                                    title="El excedente NO está en el monedero del cliente. Se acredita al aprobar el pago o al completar el pedido."
                                    className="ml-1 text-[9px] uppercase tracking-widest text-amber-500"
                                  >retenido</span>
                                )}
                              </div>
                            )}
                            {order.creditApplied > 0 && <div>Crédito: <strong className="text-purple-400">Bs {Math.round(order.creditApplied)}</strong></div>}
                          </div>

                          {/* Wallet payment info card — visible whenever credit was used */}
                          {order.creditApplied > 0 && (
                            <div
                              data-testid={`admin-order-wallet-info-${order.id}`}
                              className="p-4 rounded-2xl bg-gradient-to-br from-emerald-950/50 to-purple-950/40 border-2 border-emerald-500/40 space-y-2"
                            >
                              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                                <Wallet className="w-4 h-4" />
                                <span>Pago con Saldo a Favor</span>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <p className="text-[10px] font-mono text-zinc-500 uppercase">Saldo Disponible</p>
                                  <p className="text-sm font-black text-white font-mono" data-testid={`admin-order-wallet-available-${order.id}`}>
                                    Bs {order.creditBalanceBeforeBs != null ? Math.round(order.creditBalanceBeforeBs) : "N/D"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-mono text-zinc-500 uppercase">Saldo Utilizado</p>
                                  <p className="text-sm font-black text-emerald-300 font-mono" data-testid={`admin-order-wallet-used-${order.id}`}>
                                    Bs {Math.round(order.creditApplied)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-mono text-zinc-500 uppercase">Saldo Restante</p>
                                  <p className="text-sm font-black text-cyan-300 font-mono" data-testid={`admin-order-wallet-remaining-${order.id}`}>
                                    Bs {order.creditBalanceBeforeBs != null
                                      ? Math.round(Math.max(0, order.creditBalanceBeforeBs - order.creditApplied))
                                      : (order.userCurrentCreditBs != null ? Math.round(order.userCurrentCreditBs) : "N/D")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* All proofs uploaded for this order (primary + additional payments). */}
                          {(order.proofUrl || hasMultipleProofs) && (
                            <div className="p-3 rounded-2xl bg-ag7-bg border border-[var(--ag7-border-soft)] space-y-2" data-testid={`admin-order-proofs-${order.id}`}>
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">
                                  {hasMultipleProofs ? `Comprobantes cargados (${additionalCount + 1})` : "Comprobante"}
                                </p>
                                <p className="text-[10px] font-mono text-zinc-400">Total: <strong className="text-cyan-300">Bs {Math.round(totalPaidBs)}</strong></p>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                {order.proofUrl && (
                                  <button
                                    onClick={() => setPreviewProofUrl(order.proofUrl)}
                                    data-testid={`preview-proof-${order.id}-0`}
                                    className="group relative rounded-xl overflow-hidden border border-[var(--ag7-border-soft)] bg-ag7-surface-2 hover:border-ag7-cyan transition-all p-2 text-left"
                                    title="Ver comprobante inicial"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Eye className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-[10px] font-mono text-zinc-500 uppercase">Pago #1</p>
                                        <p className="text-xs font-bold text-white truncate">Bs {bsAmount(order.paidAmountBs)}</p>
                                        {(order.referenceNumber || order.ai_reference) && (
                                          <p className="text-[9px] font-mono text-zinc-500 truncate">Ref: {order.referenceNumber || order.ai_reference}</p>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                )}
                                {hasMultipleProofs && order.additionalPayments?.map((ap, i) => (
                                  <button
                                    key={ap.proofUrl || ap.referenceNumber || i}
                                    onClick={() => ap.proofUrl && setPreviewProofUrl(ap.proofUrl)}
                                    disabled={!ap.proofUrl}
                                    data-testid={`preview-proof-${order.id}-${i + 1}`}
                                    className="group relative rounded-xl overflow-hidden border border-[var(--ag7-border-soft)] bg-ag7-surface-2 hover:border-ag7-cyan transition-all p-2 text-left disabled:opacity-50"
                                    title="Ver comprobante adicional"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Eye className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-[10px] font-mono text-zinc-500 uppercase">Pago #{i + 2}</p>
                                        <p className="text-xs font-bold text-white truncate">Bs {Math.round(ap.paidAmountBs || 0)}</p>
                                        {(ap.referenceNumber || ap.aiReference) && (
                                          <p className="text-[9px] font-mono text-zinc-500 truncate">Ref: {ap.referenceNumber || ap.aiReference}</p>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Adjustment proof — visible when the admin has
                              requested an adjustment and the client has
                              submitted the difference-payment receipt. */}
                          {order.adjustment && order.adjustment.clientProofUrl && (
                            <div
                              className="p-3 rounded-2xl bg-amber-950/30 border border-amber-700/60 space-y-2"
                              data-testid={`admin-adjustment-proof-${order.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300">
                                  Comprobante del Ajuste
                                </p>
                                <p className="text-[10px] font-mono text-amber-200">
                                  Diferencia: <strong>Bs {Math.round(Number(order.adjustment.differenceBs || 0))}</strong>
                                </p>
                              </div>
                              <button
                                onClick={() => setPreviewProofUrl(order.adjustment.clientProofUrl)}
                                data-testid={`preview-adjustment-proof-${order.id}`}
                                className="w-full rounded-xl border border-amber-700/60 bg-amber-950/40 hover:border-amber-500 transition-all p-2 text-left flex items-center gap-2"
                                title="Ver comprobante del ajuste enviado por el cliente"
                              >
                                <Eye className="w-4 h-4 text-amber-300 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-mono text-amber-200 uppercase">Pago de Diferencia</p>
                                  <p className="text-xs font-bold text-white truncate">
                                    Bs {Math.round(Number(order.adjustment.clientPaidAmountBs || 0))}
                                    {order.adjustment.clientRef && (
                                      <span className="text-[10px] font-mono text-amber-300/70"> · Ref: {order.adjustment.clientRef}</span>
                                    )}
                                  </p>
                                </div>
                              </button>
                            </div>
                          )}
                          </div>
                        </details>
                      );
                    })()}

                    {/* Player credentials - HIGHLIGHTED, with copy buttons for admin */}
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-950/40 to-cyan-950/40 border-2 border-purple-500/40 space-y-2">
                      <div className="flex items-center gap-2 text-[10px] font-mono text-purple-300 tracking-widest uppercase">
                        <KeyRound className="w-3.5 h-3.5" />
                        <span>Datos para Recargar {order.items && order.items.length > 1 ? `(${order.items.length} recargas)` : ""}</span>
                      </div>
                      {order.items && order.items.length > 1 ? (
                        <div className="space-y-2" data-testid={`admin-order-items-list-${order.id}`}>
                          {order.items?.map((it, i) => {
                            const ct = it.credentialType || "id";
                            return (
                              <div
                                key={`${order.id}-item-${it.gameId || 'g'}-${it.packageId || 'p'}-${i}`}
                                data-testid={`admin-order-item-${order.id}-${i}`}
                                className="rounded-xl border border-purple-500/30 bg-black/30 p-3 space-y-1.5"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-[10px] font-mono uppercase tracking-widest text-purple-200">
                                    Recarga #{i + 1} · {it.gameName}
                                  </span>
                                  <span className="text-[10px] font-mono text-cyan-300">{it.packageName} · Bs {bsAmount(it.priceBs)}</span>
                                </div>
                                {ct === "username_password" ? (
                                  <>
                                    <CredRow label="Usuario" value={it.playerId} testId={`admin-order-item-user-${order.id}-${i}`} onCopy={copyToClipboard} />
                                    <CredRow label="Contraseña" value={it.playerPassword || "(no proporcionada)"} testId={`admin-order-item-pass-${order.id}-${i}`} onCopy={copyToClipboard} isPassword />
                                    {it.accessMethod && (
                                      <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-purple-900/40 border border-purple-500/50">
                                        <span className="text-[9px] font-mono uppercase tracking-widest text-purple-300">Acceso</span>
                                        <span className="text-[11px] font-black text-white">{it.accessMethod}</span>
                                      </div>
                                    )}
                                  </>
                                ) : ct === "id_server" ? (
                                  <>
                                    <CredRow label="ID Jugador" value={it.playerId} subValue={it.verifiedPlayerName} testId={`admin-order-item-id-${order.id}-${i}`} onCopy={copyToClipboard} />
                                    <CredRow label="ID Servidor" value={it.playerServer || "(no proporcionado)"} testId={`admin-order-item-server-${order.id}-${i}`} onCopy={copyToClipboard} />
                                  </>
                                ) : ct === "email" ? (
                                  <CredRow label="Correo" value={it.playerId} testId={`admin-order-item-id-${order.id}-${i}`} onCopy={copyToClipboard} />
                                ) : ct === "phone" ? (
                                  <CredRow label="Teléfono" value={it.playerId} testId={`admin-order-item-id-${order.id}-${i}`} onCopy={copyToClipboard} />
                                ) : (
                                  <CredRow label="ID Jugador" value={it.playerId} subValue={it.verifiedPlayerName} testId={`admin-order-item-id-${order.id}-${i}`} onCopy={copyToClipboard} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (order.credentialType || "id") === "username_password" ? (
                        <>
                          <CredRow label="Usuario" value={order.playerId} testId={`admin-order-playerid-${order.id}`} onCopy={copyToClipboard} />
                          <CredRow label="Contraseña" value={order.playerPassword || "(no proporcionada)"} testId={`admin-order-password-${order.id}`} onCopy={copyToClipboard} isPassword />
                          {order.accessMethod && (
                            <div
                              data-testid={`admin-order-access-method-${order.id}`}
                              className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-900/40 border border-purple-500/50"
                            >
                              <span className="text-[9px] font-mono uppercase tracking-widest text-purple-300">Forma de Acceso</span>
                              <span className="text-xs font-black text-white">{order.accessMethod}</span>
                            </div>
                          )}
                          <button
                            onClick={() => copyToClipboard(`${order.playerId} - ${order.playerPassword || ""}`, "Credenciales copiadas")}
                            data-testid={`admin-copy-both-${order.id}`}
                            className="w-full mt-2 bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-bold py-2 rounded-xl text-xs flex items-center justify-center gap-2 shadow-md">
                            <Copy className="w-4 h-4" />
                            <span>Copiar Usuario + Contraseña</span>
                          </button>
                        </>
                      ) : (order.credentialType || "id") === "id_server" ? (
                        <>
                          <CredRow
                            label="ID Jugador"
                            value={order.playerId}
                            subValue={order.verifiedPlayerName}
                            testId={`admin-order-playerid-${order.id}`}
                            onCopy={copyToClipboard}
                            big
                          />
                          <CredRow
                            label="ID Servidor"
                            value={order.playerServer || "(no proporcionado)"}
                            testId={`admin-order-server-${order.id}`}
                            onCopy={copyToClipboard}
                          />
                          <button
                            onClick={() => copyToClipboard(`${order.playerId} (${order.playerServer || ""})`, "ID + Servidor copiado")}
                            data-testid={`admin-copy-idserver-${order.id}`}
                            className="w-full mt-2 bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 rounded-xl text-xs flex items-center justify-center gap-2 shadow-md">
                            <Copy className="w-4 h-4" />
                            <span>Copiar ID + Servidor</span>
                          </button>
                        </>
                      ) : (order.credentialType || "id") === "email" ? (
                        <CredRow
                          label="Correo"
                          value={order.playerId}
                          testId={`admin-order-playerid-${order.id}`}
                          onCopy={copyToClipboard}
                          big
                        />
                      ) : (order.credentialType || "id") === "phone" ? (
                        <CredRow
                          label="Teléfono"
                          value={order.playerId}
                          testId={`admin-order-playerid-${order.id}`}
                          onCopy={copyToClipboard}
                          big
                        />
                      ) : (
                        <CredRow
                          label="ID Jugador"
                          value={order.playerId}
                          subValue={order.verifiedPlayerName}
                          testId={`admin-order-playerid-${order.id}`}
                          onCopy={copyToClipboard}
                          big
                        />
                      )}
                    </div>

                    <AdminAdjustmentPanel order={order} />

                    <OrderAutomationItems order={order} showToast={(m, t) =>
                      showToast(t === "error" ? "Automatización" : "Automatización", m, t || "success")}
                      onChanged={loadOrders} />

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ChatButton order={order} onOpen={() => setChatOrder(order)} admin />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => handleUpdateOrderStatus(order.id, "En Proceso")}
                          disabled={order.status !== "Pendiente" || hasDeliveredItem}
                          data-testid={`take-order-${order.id}`}
                          title={hasDeliveredItem
                            ? "Este pedido tiene una entrega registrada y no puede volver a tomarse."
                            : "Pasar el pedido a En Proceso"}
                          className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold disabled:opacity-40">
                          Tomar pedido
                        </button>
                        {hasDeliveredItem && ["Pendiente", "En Proceso"].includes(order.status) && (
                          <button
                            type="button"
                            onClick={() => handleConfirmRecordedDelivery(order)}
                            data-testid={`confirm-recorded-delivery-${order.orderNumber}`}
                            title="Finaliza sólo después de confirmar que el cliente recibió la recarga"
                            className="px-4 py-2 rounded-xl border border-emerald-400/70 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 text-xs font-bold"
                          >
                            Confirmar entrega
                          </button>
                        )}
                        <button onClick={() => setApprovePaymentOrder(order)}
                          disabled={order.paymentApproved || order.status === "Completado" || order.status === "Rechazado"}
                          data-testid={`approve-payment-${order.id}`}
                          title={order.paymentApproved
                            ? `Pago aprobado por ${order.paymentApprovedBy || "admin"}`
                            : "Autorizar el pago manualmente: habilita la automatización"}
                          className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5" />
                          {order.paymentApproved ? "Pago aprobado" : "Aprobar pago"}
                        </button>
                        <button onClick={() => handleUpdateOrderStatus(order.id, "Completado")}
                          disabled={order.status === "Completado" || order.status === "Rechazado"}
                          data-testid={`complete-order-${order.id}`}
                          className="px-4 py-2 rounded-xl bg-green-600 hover:bg-green-500 text-white text-xs font-bold disabled:opacity-40">
                          Recarga realizada
                        </button>
                        <button onClick={() => openRejectOrder(order)}
                          disabled={order.status === "Completado" || order.status === "Rechazado"}
                          data-testid={`reject-order-${order.id}`}
                          className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1.5">
                          <XCircle className="w-3.5 h-3.5" />
                          Rechazar
                        </button>
                        {order.status !== "Rechazado" && order.status !== "Completado" && order.status !== "Ajuste Enviado" && (
                          <button onClick={() => setAdjustOrderModal(order)}
                            data-testid={`adjust-order-${order.id}`}
                            title="Cambiar el pedido por otro paquete y cobrar la diferencia"
                            className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5" />
                            Solicitar Ajuste
                          </button>
                        )}
                        {order.status === "Ajuste Enviado" && (
                          <button onClick={() => handleApproveAdjustment(order.id)}
                            data-testid={`approve-adjustment-${order.id}`}
                            title="Aprobar el pago de la diferencia enviado por el cliente; el pedido pasa a En Proceso"
                            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Aprobar Diferencia
                          </button>
                        )}
                        {(order.status === "Completado" || order.status === "Rechazado") && (
                          <button
                            onClick={() => handleReopenOrder(order.id)}
                            data-testid={`reopen-order-${order.id}`}
                            title="Volver a poner el pedido en proceso y reabrir el chat con el cliente"
                            className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-amber-500/30"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Reabrir Pedido
                          </button>
                        )}
                      </div>
                    </div>
                    {order.status === "Rechazado" && order.rejectionReason && (
                      <div className="p-3 rounded-xl bg-red-950/40 border border-red-500/40 text-xs text-red-200">
                        <p className="font-bold mb-1 text-red-300 uppercase tracking-widest text-[10px] font-mono">Motivo del rechazo</p>
                        <p className="text-red-100">{order.rejectionReason}</p>
                      </div>
                    )}
                    </div>
                  </div>
                );
                });
                })()}
                {/* Cursor-based "Cargar más" — appends next 15 rows using the
                    oldest currently-loaded createdAt as cursor. Hidden when
                    the last page didn't return a full batch. Visible on every
                    chip / search state so admins can broaden the search pool. */}
                {adminHasMoreOrders && orders.length > 0 && (
                  <div className="pt-2 flex flex-col items-center gap-1">
                    <button
                      type="button"
                      onClick={loadMoreAdminOrders}
                      disabled={adminLoadMoreBusy}
                      data-testid="admin-load-more-orders-btn"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border border-cyan-500/40 bg-cyan-500/[0.06] hover:bg-cyan-500/15 hover:border-cyan-300 text-cyan-100 text-xs font-black uppercase tracking-widest transition-all shadow-[0_0_18px_-8px_rgba(34,211,238,0.6)] hover:shadow-[0_0_28px_-4px_rgba(34,211,238,0.9)] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {adminLoadMoreBusy ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <span className="w-3.5 h-3.5 rounded-full border border-cyan-400/70" aria-hidden="true" />
                      )}
                      <span>{adminLoadMoreBusy ? "Cargando…" : "Cargar más pedidos"}</span>
                    </button>
                    {searchQuery && (
                      <p className="text-[10px] font-mono text-zinc-500">
                        ¿No encontraste lo que buscas? Carga más pedidos históricos.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* PACKAGES ADMIN */}
            {adminTab === "paquetes" && (
              <CatalogManagement />
            )}

            {/* PAYMENT METHODS ADMIN */}
            {adminTab === "pagos" && (
              <AdminPagosTab
                paymentMethods={paymentMethods}
                onOpenAdd={() => { setEditingPm(null); setBankForm(EMPTY_BANK_FORM); setShowAddBankModal(true); }}
                onEdit={openEditPm}
                onToggleActive={handleTogglePmActive}
                onDelete={handleDeleteBank}
              />
            )}

            {/* USERS ADMIN */}
            {adminTab === "usuarios" && (
              <UserManagement />
            )}

            {/* RETIROS DE REVENDEDORES — módulo independiente de pedidos */}
            {adminTab === "retiros" && (
              <WithdrawalsPanel showToast={showToast} />
            )}

            {adminTab === "torneos" && (
              <TournamentsAdminPanel showToast={showToast} />
            )}

            {/* TOP USERS RANKING */}
            {adminTab === "top" && (
              <TopUsersRanking />
            )}

            {/* GANANCIAS — Supabase-backed sales/profit dashboard */}
            {adminTab === "ganancias" && (
              <SalesPanel />
            )}

            {/* EXCHANGE RATE */}
            {adminTab === "divisa" && (
              <AdminDivisaTab
                rateInput={rateInput}
                setRateInput={setRateInput}
                onSaveRate={handleSaveRate}
                supportWhatsappInput={supportWhatsappInput}
                setSupportWhatsappInput={setSupportWhatsappInput}
                supportWhatsapp={supportWhatsapp}
                savingSupportWa={savingSupportWa}
                onSaveSupportWhatsapp={handleSaveSupportWhatsapp}
                showToast={showToast}
                activeRate={exchangeRate}
                onRateChanged={(r) => {
                  const v = Number(r);
                  if (!Number.isFinite(v) || v <= 0) return;
                  setExchangeRate(v);
                  setRateInput(String(v));
                  try { window.localStorage?.setItem("ag7_exchange_rate", String(v)); }
                  catch (e) { console.warn("exchange_rate write failed:", e); }
                }}
              />
            )}

            {/* REDES SOCIALES (own tab) */}
            {adminTab === "redes" && (
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-4" data-testid="admin-social-networks-section">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-pink-950/60 border border-pink-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-pink-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Redes Sociales</h3>
                      <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                        Estas se muestran al cliente en la notificación de &quot;Recarga realizada&quot; con el mensaje
                        &quot;Síguenos para mantenerte informado&quot;. Puedes activar/desactivar cualquiera sin borrarla.
                      </p>
                    </div>
                  </div>

                  {/* Add / edit form */}
                  <form data-testid="admin-page-form-2" onSubmit={handleSaveSocial} className="space-y-2 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-2xl p-4">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-pink-300">
                      {snForm.id ? "Editar red social" : "Añadir red social"}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={snForm.name}
                        onChange={(e) => setSnForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Nombre (ej. WhatsApp Canal)"
                        data-testid="sn-name-input"
                        className="bg-ag7-surface-2 border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-magenta"
                        maxLength={60}
                      />
                      <input
                        type="url"
                        value={snForm.url}
                        onChange={(e) => setSnForm(f => ({ ...f, url: e.target.value }))}
                        placeholder="https://…"
                        data-testid="sn-url-input"
                        className="md:col-span-2 bg-ag7-surface-2 border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-magenta"
                        maxLength={500}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={snForm.icon}
                        onChange={(e) => setSnForm(f => ({ ...f, icon: e.target.value }))}
                        placeholder="Emoji opcional (📢, 💬…)"
                        data-testid="sn-icon-input"
                        className="w-40 bg-ag7-surface-2 border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-magenta"
                        maxLength={4}
                      />
                      <button
                        type="submit"
                        disabled={snBusy || !snForm.name.trim() || !snForm.url.trim()}
                        data-testid="sn-save-btn"
                        className="bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-2"
                      >
                        {snBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        <span>{snForm.id ? "Actualizar" : "Añadir"}</span>
                      </button>
                      {snForm.id && (
                        <button data-testid="admin-page-button-3"
                          type="button"
                          onClick={() => setSnForm({ id: null, name: "", url: "", icon: "" })}
                          className="bg-ag7-surface-2 hover:bg-zinc-700 text-zinc-300 font-bold px-3 py-2 rounded-lg text-xs"
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </form>

                  {/* List */}
                  <div className="space-y-2">
                    {adminSocialNets.length === 0 ? (
                      <p className="text-center text-xs text-zinc-500 py-4 font-mono">Aún no hay redes sociales. Añade la primera arriba ↑</p>
                    ) : (
                      adminSocialNets?.map(net => (
                        <div
                          key={net.id}
                          data-testid={`sn-row-${net.id}`}
                          className={`flex items-center gap-3 p-3 rounded-xl border ${net.active ? "bg-zinc-950 border-zinc-800" : "bg-ag7-bg/50 border-zinc-800/60 opacity-60"}`}
                        >
                          <span className="text-xl flex-shrink-0">{net.icon || "🔗"}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-white truncate">{net.name}</p>
                            <a data-testid="admin-page-a-4"
                              href={net.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] font-mono text-cyan-400 hover:underline truncate block"
                            >
                              {net.url}
                            </a>
                          </div>
                          <label className="inline-flex items-center gap-2 cursor-pointer text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                            <input
                              type="checkbox"
                              checked={net.active}
                              onChange={() => handleToggleSocial(net)}
                              data-testid={`sn-toggle-${net.id}`}
                              className="w-4 h-4 accent-pink-500"
                            />
                            <span className={net.active ? "text-green-300" : "text-zinc-500"}>
                              {net.active ? "Activa" : "Inactiva"}
                            </span>
                          </label>
                          <button
                            onClick={() => setSnForm({ id: net.id, name: net.name, url: net.url, icon: net.icon || "" })}
                            data-testid={`sn-edit-${net.id}`}
                            className="p-2 rounded-lg bg-ag7-surface-2 hover:bg-zinc-700 text-cyan-300"
                            title="Editar"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteSocial(net)}
                            data-testid={`sn-delete-${net.id}`}
                            className="p-2 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* BANNERS admin tab */}
            {adminTab === "banners" && (
              <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-banners-view">
                {/* Create/Edit form */}
                <form data-testid="admin-page-form-5" onSubmit={handleSaveBanner} className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-indigo-950/60 border border-indigo-500 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">
                        {bannerForm.id ? "Editar banner" : "Nuevo banner promocional"}
                      </h3>
                      <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                        Solo puede haber <strong>un banner activo por juego</strong>. Al activar uno, cualquier otro del mismo juego se desactiva automáticamente.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Juego *</label>
                      <select
                        value={bannerForm.game_id}
                        onChange={(e) => setBannerForm(f => ({ ...f, game_id: e.target.value }))}
                        disabled={!!bannerForm.id}
                        data-testid="banner-game-select"
                        className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60 focus:outline-none focus:border-ag7-purple"
                      >
                        <option value="">— Selecciona un juego —</option>
                        {games?.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Título *</label>
                      <input
                        type="text"
                        value={bannerForm.title}
                        onChange={(e) => setBannerForm(f => ({ ...f, title: e.target.value }))}
                        maxLength={120}
                        placeholder="Ej. Nuevo pack Free Fire 100+10"
                        data-testid="banner-title-input"
                        className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-purple"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Descripción</label>
                    <textarea
                      value={bannerForm.description}
                      onChange={(e) => setBannerForm(f => ({ ...f, description: e.target.value.slice(0, 500) }))}
                      rows={2}
                      maxLength={500}
                      placeholder="Opcional — se mostrará debajo del título del banner en la tienda"
                      data-testid="banner-description-input"
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-ag7-purple resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
                      Imágenes ({bannerForm.images.length}/5)
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {bannerForm.images?.map((url, i) => (
                        <div key={`banner-img-${i}-${url.slice(-20)}`} className="relative aspect-video rounded-lg overflow-hidden border border-[var(--ag7-border-soft)] bg-[#0d1117]">
                          <img src={url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg brightness-50" />
                          <img src={url} alt={`banner-img-${i}`} className="relative w-full h-full object-contain z-[1]" style={{ imageRendering: "-webkit-optimize-contrast" }} data-testid={`banner-form-image-${i}`} />
                          <button
                            type="button"
                            onClick={() => handleRemoveBannerImage(i)}
                            data-testid={`banner-form-image-remove-${i}`}
                            className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px]"
                            aria-label="Quitar imagen"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {bannerForm.images.length < 5 && (
                        <label className="aspect-video rounded-lg border-2 border-dashed border-zinc-700 hover:border-indigo-500 flex flex-col items-center justify-center gap-1 cursor-pointer bg-ag7-bg/50 hover:bg-indigo-500/10 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleBannerImageUpload(e.target.files?.[0])}
                            data-testid="banner-form-image-input"
                          />
                          {bannerUploadingIdx
                            ? <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />
                            : <Upload className="w-5 h-5 text-zinc-500" />}
                          <span className="text-[10px] font-mono text-zinc-500 uppercase">Subir</span>
                        </label>
                      )}
                    </div>
                    <p className="text-[10px] font-mono text-zinc-500 mt-1">
                      Hasta 5 imágenes. Máx 4 MB c/u. Cualquier proporción — las verticales muestran un fondo desenfocado a los lados.
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)]">
                    <label htmlFor="banner-active-toggle" className="text-sm text-white font-bold cursor-pointer flex items-center gap-2">
                      <ShieldCheck className={`w-4 h-4 ${bannerForm.is_active ? "text-green-400" : "text-zinc-500"}`} />
                      <span>Activar banner</span>
                    </label>
                    <input
                      id="banner-active-toggle"
                      type="checkbox"
                      checked={!!bannerForm.is_active}
                      onChange={(e) => setBannerForm(f => ({ ...f, is_active: e.target.checked }))}
                      data-testid="banner-active-checkbox"
                      className="w-5 h-5 accent-green-500 cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 p-3 rounded-xl border border-orange-400/35 bg-orange-500/10">
                    <label htmlFor="banner-fire-toggle" className="text-sm text-white font-bold cursor-pointer flex items-center gap-2">
                      <Flame className={`w-4 h-4 ${bannerForm.fire_effect ? "text-orange-300" : "text-zinc-500"}`} />
                      <span>Fuego intenso de evento</span>
                    </label>
                    <input id="banner-fire-toggle" type="checkbox" checked={!!bannerForm.fire_effect} onChange={(e) => setBannerForm(f => ({ ...f, fire_effect: e.target.checked }))} data-testid="banner-fire-effect-checkbox" className="w-5 h-5 accent-orange-500 cursor-pointer" />
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--ag7-border-soft)]">
                    <button
                      type="submit"
                      disabled={bannerBusy}
                      data-testid="banner-save-btn"
                      className="bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 disabled:opacity-40 text-white font-bold px-6 py-2 rounded-xl text-xs flex items-center gap-2"
                    >
                      {bannerBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      <span>{bannerForm.id ? "Actualizar banner" : "Crear banner"}</span>
                    </button>
                    {bannerForm.id && (
                      <button data-testid="admin-page-button-6"
                        type="button"
                        onClick={resetBannerForm}
                        className="bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold px-4 py-2 rounded-xl text-xs"
                      >
                        Cancelar edición
                      </button>
                    )}
                  </div>
                </form>

                {/* List of banners */}
                <div className="space-y-3">
                  <h4 className="text-sm font-mono uppercase tracking-widest text-zinc-400">
                    Banners ({banners.length})
                  </h4>
                  {banners.length === 0 ? (
                    <p className="text-center text-xs text-zinc-500 py-6 font-mono">No hay banners aún.</p>
                  ) : banners?.map(b => {
                    const game = games?.find(g => g.id === b.game_id);
                    return (
                      <div key={b.id} data-testid={`banner-row-${b.id}`} className="rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-4 flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-zinc-800 text-cyan-300">
                              {game?.name || "Juego eliminado"}
                            </span>
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${b.is_active ? "bg-green-950/60 border border-green-500 text-green-300" : "bg-ag7-surface-2 border border-[var(--ag7-border-soft)] text-zinc-400"}`}>
                              {b.is_active ? "ACTIVO" : "INACTIVO"}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-white">{b.title}</p>
                          {b.description && <p className="text-[11px] text-zinc-400">{b.description}</p>}
                          {b.images?.length > 0 && (
                            <div className="flex gap-1.5 flex-wrap">
                              {b.images.slice(0, 5).map((url, i) => (
                                <img key={`${b.id}-img-${i}-${url.slice(-16)}`} src={url} alt="" className="w-14 h-10 rounded-md object-cover border border-[var(--ag7-border-soft)]" />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex sm:flex-col items-center gap-2 flex-shrink-0">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] font-mono uppercase text-zinc-400">
                            <input
                              type="checkbox"
                              checked={b.is_active}
                              onChange={() => handleToggleBannerActive(b)}
                              data-testid={`banner-toggle-${b.id}`}
                              className="w-4 h-4 accent-green-500"
                            />
                            <span>{b.is_active ? "ON" : "OFF"}</span>
                          </label>
                          <button
                            onClick={() => setBannerForm({
                              id: b.id,
                              game_id: b.game_id,
                              title: b.title,
                              description: b.description || "",
                              images: b.images || [],
                              is_active: b.is_active,
                            })}
                            data-testid={`banner-edit-${b.id}`}
                            className="p-2 rounded-lg bg-ag7-surface-2 hover:bg-zinc-700 text-cyan-300"
                            title="Editar"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteBanner(b)}
                            data-testid={`banner-delete-${b.id}`}
                            className="p-2 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SEGURIDAD (2FA + Cambiar contraseña) */}
            {adminTab === "seguridad" && (
              <div className="max-w-xl mx-auto space-y-6" data-testid="admin-security-view">
                {/* --- Pre-launch: reset test data card --- */}
                <ResetTestDataCard />

                {/* --- 2FA card --- */}
                <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-purple-950/60 border border-purple-500 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Verificación en dos pasos (2FA)</h3>
                        <span data-testid="twofa-status-badge" className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${twoFAEnabled ? "bg-green-950/60 border border-green-500 text-green-300" : "bg-ag7-surface-2 border border-[var(--ag7-border-soft)] text-zinc-400"}`}>
                          {twoFAEnabled ? "ACTIVADO" : "DESACTIVADO"}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-1">
                        Añade una capa extra usando Google Authenticator, Authy o cualquier app compatible con TOTP.
                      </p>
                    </div>
                  </div>

                  {!twoFAEnabled && !twoFASetup && (
                    <button
                      onClick={handleStartTwoFASetup}
                      data-testid="twofa-start-setup-btn"
                      className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span>ACTIVAR 2FA</span>
                    </button>
                  )}

                  {!twoFAEnabled && twoFASetup && (
                    <form data-testid="admin-page-form-7" onSubmit={handleEnableTwoFA} className="space-y-4">
                      <div className="p-4 rounded-2xl bg-ag7-bg border border-[var(--ag7-border-soft)] space-y-3">
                        <p className="text-[11px] text-zinc-400">
                          1. Abre tu app de autenticación y escanea el código QR o pega el secreto manualmente.
                        </p>
                        <div className="flex justify-center bg-white rounded-xl p-3">
                          <img
                            alt="QR 2FA"
                            data-testid="twofa-qr-img"
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFASetup.otpauth_uri)}`}
                            className="w-40 h-40"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-mono uppercase text-zinc-500 mb-1">Secreto (base32)</label>
                          <div className="flex gap-2">
                            <code data-testid="twofa-secret" className="flex-1 text-xs font-mono text-cyan-300 bg-ag7-surface-2 border border-[var(--ag7-border-soft)] rounded-lg px-3 py-2 break-all">
                              {twoFASetup.secret}
                            </code>
                            <button
                              type="button"
                              onClick={() => { navigator.clipboard.writeText(twoFASetup.secret); showToast("Copiado", "Secreto copiado al portapapeles", "success"); }}
                              data-testid="twofa-copy-secret-btn"
                              className="bg-ag7-surface-2 hover:bg-zinc-700 text-white px-3 rounded-lg"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
                          2. Ingresa el código de 6 dígitos de tu app
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          data-testid="twofa-setup-code-input"
                          value={twoFASetupCode}
                          onChange={(e) => setTwoFASetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="123456"
                          className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-2xl font-mono text-center tracking-[0.5em] focus:outline-none focus:border-ag7-purple"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button data-testid="admin-page-button-8"
                          type="button"
                          onClick={() => { setTwoFASetup(null); setTwoFASetupCode(""); }}
                          className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs"
                        >Cancelar</button>
                        <button
                          type="submit"
                          disabled={twoFAEnableBusy || twoFASetupCode.length !== 6}
                          data-testid="twofa-confirm-enable-btn"
                          className="flex-1 bg-gradient-to-r from-green-600 to-green-500 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2"
                        >
                          {twoFAEnableBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          <span>{twoFAEnableBusy ? "Activando..." : "Confirmar y activar"}</span>
                        </button>
                      </div>
                    </form>
                  )}

                  {twoFAEnabled && (
                    <form data-testid="admin-page-form-9" onSubmit={handleDisableTwoFA} className="space-y-3">
                      <p className="text-[11px] text-zinc-400">
                        Para desactivar la verificación en dos pasos ingresa tu contraseña actual.
                      </p>
                      <input
                        type="password"
                        placeholder="Contraseña actual"
                        value={twoFADisablePwd}
                        onChange={(e) => setTwoFADisablePwd(e.target.value)}
                        data-testid="twofa-disable-password"
                        className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-red-500"
                      />
                      <button
                        type="submit"
                        disabled={twoFADisableBusy || !twoFADisablePwd}
                        data-testid="twofa-disable-btn"
                        className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2"
                      >
                        {twoFADisableBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        <span>{twoFADisableBusy ? "Procesando..." : "Desactivar 2FA"}</span>
                      </button>
                    </form>
                  )}
                </div>

                {/* --- Site open/closed card --- */}
                <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5" data-testid="admin-site-status-card">
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 ${siteStatus.open ? "bg-emerald-950/60 border-emerald-500" : "bg-red-950/60 border-red-500"}`}>
                      {siteStatus.open
                        ? <Check className="w-5 h-5 text-emerald-400" />
                        : <Lock className="w-5 h-5 text-red-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Tienda abierta / cerrada</h3>
                        <span
                          data-testid="site-status-badge"
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${siteStatus.open ? "bg-green-950/60 border border-green-500 text-green-300" : "bg-red-950/60 border border-red-500 text-red-300"}`}
                        >
                          {siteStatus.open ? "ABIERTA" : "CERRADA"}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                        Cuando desactivas la tienda, los clientes que ingresen ven un mensaje de mantenimiento y no pueden comprar. El panel admin siempre queda accesible.
                      </p>
                    </div>
                  </div>

                  {/* Big on/off switch */}
                  <button
                    type="button"
                    onClick={handleToggleSiteOpen}
                    disabled={siteStatusBusy}
                    data-testid="site-status-toggle-btn"
                    role="switch"
                    aria-checked={siteStatus.open}
                    className={`relative w-full h-16 rounded-2xl border-2 transition-all flex items-center justify-between px-5 font-bold text-sm ${siteStatus.open
                      ? "bg-gradient-to-r from-emerald-600/20 to-green-600/20 border-emerald-500 text-emerald-100"
                      : "bg-gradient-to-r from-red-800/25 to-red-700/20 border-red-500 text-red-100"
                    } disabled:opacity-60`}
                  >
                    <span className="flex items-center gap-2">
                      {siteStatusBusy ? <RefreshCw className="w-5 h-5 animate-spin" /> :
                        (siteStatus.open ? <Check className="w-5 h-5" /> : <Lock className="w-5 h-5" />)}
                      <span>{siteStatus.open ? "Tienda ABIERTA — pulsa para cerrar" : "Tienda CERRADA — pulsa para abrir"}</span>
                    </span>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${siteStatus.open ? "bg-emerald-500/25" : "bg-red-500/25"}`}>
                      {siteStatus.open ? "ON" : "OFF"}
                    </span>
                  </button>

                  {/* Reason */}
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
                      Motivo / mensaje que verán los clientes
                    </label>
                    <textarea
                      value={siteStatusForm.reason}
                      onChange={(e) => setSiteStatusForm({ reason: e.target.value.slice(0, 500) })}
                      data-testid="site-status-reason-input"
                      rows={3}
                      maxLength={500}
                      placeholder="Ej. Estamos actualizando el catálogo. Volvemos en 1 hora."
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-red-500 resize-none"
                    />
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-[10px] font-mono text-zinc-500">
                        {siteStatusForm.reason.length}/500
                      </span>
                      <button
                        type="button"
                        onClick={handleSaveClosureReason}
                        disabled={siteStatusBusy || siteStatusForm.reason === siteStatus.reason}
                        data-testid="site-status-save-reason-btn"
                        className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-2"
                      >
                        {siteStatusBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        <span>Guardar motivo</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* --- Cierre automático nocturno por inactividad --- */}
                <AutoCloseCard showToast={showToast} onSiteStatusChanged={loadSiteStatus} />

                {/* --- Change password card --- */}
                <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-cyan-950/60 border border-cyan-500 flex items-center justify-center flex-shrink-0">
                      <KeyRound className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Cambiar contraseña</h3>
                      <p className="text-[11px] text-zinc-400 mt-1">Recomendado cada 90 días. Mínimo 8 caracteres.</p>
                    </div>
                  </div>
                  <form data-testid="admin-page-form-10" onSubmit={handleChangePassword} className="space-y-3">
                    <input
                      type="password"
                      placeholder="Contraseña actual"
                      value={pwdOld}
                      onChange={(e) => setPwdOld(e.target.value)}
                      data-testid="change-password-old"
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-cyan-500"
                    />
                    <input
                      type="password"
                      placeholder="Nueva contraseña (mín. 8)"
                      value={pwdNew}
                      onChange={(e) => setPwdNew(e.target.value)}
                      data-testid="change-password-new"
                      className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-cyan-500"
                    />
                    <button
                      type="submit"
                      disabled={pwdBusy || !pwdOld || pwdNew.length < 8}
                      data-testid="change-password-submit-btn"
                      className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2"
                    >
                      {pwdBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      <span>{pwdBusy ? "Guardando..." : "Actualizar contraseña"}</span>
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>

      {/* 💳 CONFIRMACIÓN DE APROBACIÓN FINANCIERA DEL PAGO */}
      {approvePaymentOrder && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          data-testid="approve-payment-modal">
          <div className="w-full max-w-md bg-[#12121a] border border-indigo-400/40 rounded-2xl p-6 space-y-4 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <div>
              <h3 className="font-black text-white uppercase tracking-wide text-sm">💳 Aprobar pago</h3>
              <p className="text-xs text-zinc-400 mt-1">
                Confirmas que revisaste el comprobante manualmente y el pago es correcto.
                Esta autorización habilita la automatización de la recarga.
              </p>
            </div>
            <div className="text-xs font-mono space-y-1 text-zinc-300">
              <p>Pedido: <span className="text-white">{approvePaymentOrder.id}</span></p>
              <p>Juego: <span className="text-white">{approvePaymentOrder.gameName}</span></p>
              <p>Producto: <span className="text-white">{approvePaymentOrder.packageName}</span></p>
              <p>ID jugador: <span className="text-white">{approvePaymentOrder.playerId}</span></p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setApprovePaymentOrder(null)} disabled={approvePaymentBusy}
                data-testid="approve-payment-cancel"
                className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-700 text-zinc-300 text-sm font-bold disabled:opacity-50">
                Cancelar
              </button>
              <button
                data-testid="approve-payment-confirm"
                disabled={approvePaymentBusy}
                onClick={async () => {
                  setApprovePaymentBusy(true);
                  try {
                    const res = await api.approveOrderPayment(approvePaymentOrder.id);
                    const messages = {
                      MANUAL_PROMO_DETECTED: "🎁 Promoción detectada en PagoStore: recarga manual requerida",
                      MANUAL_PROMO_UNKNOWN: "⚠️ No se pudo verificar la promoción: revisión manual",
                      SUPERVISED_READY: "Pago aprobado · operación lista para ejecutar",
                      AUTOMATIC_EXECUTED: "Pago aprobado · recarga enviada automáticamente",
                      MANUAL_NOT_ELIGIBLE: "Pago aprobado · no automatizable",
                      ALREADY_APPROVED: "Este pago ya estaba aprobado",
                      BLOCKED: "Pago aprobado · automatización detenida para este pedido",
                    };
                    const base = messages[res.flow] || "Pago aprobado";
                    showToast("Pago aprobado", res.reason ? `${base}: ${res.reason}` : base, "success");
                    setOrders((prev) => prev?.map((o) => (o.id === approvePaymentOrder.id
                      ? { ...o, paymentApproved: true, paymentApprovedBy: res.payment_approved_by } : o)));
                    setApprovePaymentOrder(null);
                  } catch (e) {
                    showToast("Error", e?.message || "No se pudo aprobar el pago", "error");
                  } finally {
                    setApprovePaymentBusy(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-black disabled:opacity-50">
                {approvePaymentBusy ? "Aprobando…" : "Confirmar aprobación"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}